<script setup lang="ts">
import { computed, ref, useAttrs, useId, watch } from 'vue'
import { FairIcon } from '@/ui/primitives/FairIcon'
import { FairInputListItem } from '@/ui/primitives/FairInputListItem'
import { FairInputText } from '@/ui/primitives/FairInputText'
import type {
  FairDropdownOption,
  FairDropdownOptions,
} from '@/ui/tokens/inputs.data'
import { filterDropdownSource } from './fairDropdownFilter'
import {
  findDropdownOption,
  flattenDropdownOptions,
} from './fairDropdownFlat'

defineOptions({ inheritAttrs: false })

interface FairDropdownProps {
  modelValue?: string | null
  options?: FairDropdownOptions
  placeholder?: string
  rounded?: boolean
  filter?: boolean
  filterPlaceholder?: string
  /** Radio rows — Select / sort (Figma closes on pick, no footer). */
  showRadio?: boolean
  /** When false, hide the trailing caret (e.g. compact search sort). Default true. */
  showChevron?: boolean
  disabled?: boolean
  invalid?: boolean
  inputId?: string
}

const props = withDefaults(defineProps<FairDropdownProps>(), {
  modelValue: null,
  options: () => [],
  placeholder: 'Select a Country',
  rounded: false,
  filter: false,
  filterPlaceholder: '',
  showRadio: false,
  showChevron: true,
  disabled: false,
  invalid: false,
  inputId: undefined,
})

const emit = defineEmits<{
  'update:modelValue': [value: string | null]
  change: [option: FairDropdownOption]
  filter: [query: string]
}>()

const attrs = useAttrs()
const isOpen = ref(false)
const filterQuery = ref('')
const highlightedIndex = ref(-1)
const rootRef = ref<HTMLElement | null>(null)

/** Keep class/style on root (flex layout); forward the rest to the trigger. */
const buttonAttrs = computed(() => {
  const { class: _class, style: _style, ...rest } = attrs as Record<string, unknown>
  return rest
})

const generatedListboxId = useId()
const listboxId = computed(() => props.inputId
  ? `${props.inputId}-listbox`
  : `fair-dropdown-listbox-${generatedListboxId}`)

const filteredSource = computed(() => {
  if (!props.filter) {
    return props.options
  }

  return filterDropdownSource(props.options, filterQuery.value)
})

const flatItems = computed(() => flattenDropdownOptions(filteredSource.value))

const selectableItems = computed(() =>
  flatItems.value.filter((item) => item.kind === 'option' && item.option),
)

const selectedOption = computed(() =>
  findDropdownOption(props.options, props.modelValue),
)

const displayLabel = computed(() => selectedOption.value?.label ?? '')

const triggerClass = computed(() => ({
  'fair-dropdown__trigger': true,
  'fair-dropdown__trigger--rounded': props.rounded,
  'fair-dropdown__trigger--open': isOpen.value,
  'fair-dropdown__trigger--invalid': props.invalid,
  'fair-dropdown__trigger--disabled': props.disabled,
}))

watch(filteredSource, () => {
  highlightedIndex.value = selectableItems.value.length ? 0 : -1
})

watch(filterQuery, (query) => {
  emit('filter', query)
})

function openPanel() {
  if (props.disabled) {
    return
  }

  isOpen.value = true
  highlightedIndex.value = selectableItems.value.length ? 0 : -1
}

function closePanel() {
  isOpen.value = false
  highlightedIndex.value = -1

  if (props.filter) {
    filterQuery.value = ''
  }
}

function togglePanel() {
  if (isOpen.value) {
    closePanel()
    return
  }

  openPanel()
}

function handleFocusOut(event: FocusEvent) {
  const related = event.relatedTarget as Node | null

  if (rootRef.value?.contains(related)) {
    return
  }

  window.setTimeout(() => {
    closePanel()
  }, 120)
}

function handleSelectOption(option: FairDropdownOption) {
  if (option.disabled) {
    return
  }

  emit('update:modelValue', option.key)
  emit('change', option)
  closePanel()
}

function moveHighlight(delta: number) {
  const count = selectableItems.value.length

  if (!count) {
    highlightedIndex.value = -1
    return
  }

  if (highlightedIndex.value < 0) {
    highlightedIndex.value = 0
    return
  }

  highlightedIndex.value = (highlightedIndex.value + delta + count) % count
}

function handleKeydown(event: KeyboardEvent) {
  if (!isOpen.value) {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      openPanel()
      event.preventDefault()
    }

    return
  }

  if (event.key === 'ArrowDown') {
    moveHighlight(1)
    event.preventDefault()
    return
  }

  if (event.key === 'ArrowUp') {
    moveHighlight(-1)
    event.preventDefault()
    return
  }

  if (event.key === 'Escape') {
    closePanel()
    event.preventDefault()
    return
  }

  if (event.key === 'Enter') {
    const item = selectableItems.value[highlightedIndex.value]

    if (item?.option) {
      handleSelectOption(item.option)
      event.preventDefault()
    }
  }
}

function isHighlighted(flatIndex: number): boolean {
  const item = flatItems.value[flatIndex]

  if (!item || item.kind !== 'option') {
    return false
  }

  const selectableIndex = selectableItems.value.findIndex(
    (selectable) => selectable.key === item.key,
  )

  return selectableIndex === highlightedIndex.value
}

function isSelected(key: string): boolean {
  return props.modelValue === key
}
</script>

<template>
  <div
    ref="rootRef"
    class="fair-dropdown"
    :class="[
      {
        'fair-dropdown--open': isOpen,
        'fair-dropdown--rounded': rounded,
        'fair-dropdown--radio': showRadio,
      },
      attrs.class,
    ]"
    :style="attrs.style as string | Record<string, string> | undefined"
    @focusout="handleFocusOut"
    @keydown="handleKeydown"
  >
    <button
      :id="inputId"
      type="button"
      v-bind="buttonAttrs"
      :class="triggerClass"
      role="combobox"
      aria-haspopup="listbox"
      :aria-expanded="isOpen"
      :aria-controls="listboxId"
      :disabled="disabled"
      @click="togglePanel"
    >
      <span
        v-if="$slots.leading"
        class="fair-dropdown__leading"
        aria-hidden="true"
      >
        <slot name="leading" />
      </span>

      <span
        class="fair-dropdown__label fair-text-input"
        :class="{ 'fair-dropdown__label--placeholder': !displayLabel }"
      >
        {{ displayLabel || placeholder }}
      </span>

      <span
        v-if="showChevron"
        class="fair-dropdown__chevron"
        :class="{ 'fair-dropdown__chevron--open': isOpen }"
        aria-hidden="true"
      >
        <span class="fair-dropdown__chevron-graphic">
          <FairIcon
            name="caret-down-field"
            color="var(--fair-color-primary-blue-100)"
          />
        </span>
      </span>
    </button>

    <div
      v-if="isOpen"
      class="fair-dropdown__panel"
      :class="{ 'fair-dropdown__panel--rounded': rounded || showRadio }"
    >
      <div
        v-if="filter"
        class="fair-dropdown__header"
      >
        <FairInputText
          v-model="filterQuery"
          :placeholder="filterPlaceholder"
          right-icon="search"
          :rounded="false"
          @click.stop
        />
      </div>

      <div
        v-if="$slots['header-action']"
        class="fair-dropdown__header-action"
      >
        <slot name="header-action" />
      </div>

      <div
        :id="listboxId"
        class="fair-dropdown__list"
        role="listbox"
      >
        <template
          v-for="(item, index) in flatItems"
          :key="item.key"
        >
          <FairInputListItem
            v-if="item.kind === 'group' && item.group"
            group
            :label="item.group.label"
            :emoji="item.group.emoji"
            :icon="item.group.icon"
            :interactive="false"
          />

          <FairInputListItem
            v-else-if="item.option"
            :label="item.option.label"
            :description="item.option.description"
            :icon="item.option.icon"
            :disabled="item.option.disabled"
            :radio="showRadio"
            :radio-selected="showRadio && isSelected(item.option.key)"
            :highlighted="!showRadio && isHighlighted(index)"
            @select="handleSelectOption(item.option)"
          />
        </template>
      </div>
    </div>
  </div>
</template>
